import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const PostToolbar = ({
  editorState,
  onToggleInlineStyle,
  onToggleBlockType,
  onExpandEditor,
  onFileSelect,
  onEmojiSelect,
  onBookmarkIcon,
  onUndo,
  onRedo,
  onFullscreen,
  onClose,
  isExpanded,
  showEmojiPicker,
  emojiPickerRef,
  showTagModal,
  members,
  isLoadingMembers = false,
  handleTagUserClicked,
  userProfile,
  tagDropdownRef,
  variant = "default", // "default" or "compact"
  enableMediaActions = true, // show/hide file/image/gif actions
  emojiPlacement = "below", // "below" | "above"
  /** When true, parent renders the tag/member list (e.g. comment mention portal); toolbar only shows the button. */
  externalTagPopover = false,
}) => {
  const toolbarRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const emojiPopoverRef = useRef(null);
  const tagAnchorRef = useRef(null);
  const [tagPopoverPos, setTagPopoverPos] = useState({
    left: 0,
    top: 0,
    width: 280,
    maxHeight: 300,
  });
  const [emojiPopoverPos, setEmojiPopoverPos] = useState({
    left: 0,
    top: 0,
    placement: "below",
  });

  // Undo/Redo logic if not passed
  const handleUndo = () => {
    if (onUndo) onUndo();
  };

  const handleRedo = () => {
    if (onRedo) onRedo();
  };

  const formattingButtons = [
    {
      action: "file",
      icon: <FileSelectorIcon />,
      label: "Add Image",
      showInCompact: true,
    },
    {
      style: "BOLD",
      icon: (
        <img
          src="/icons/bold.svg"
          alt="Bold"
          style={{
            width: "18px",
            height: "18px",
            filter: "brightness(0) invert(1)",
          }}
        />
      ),
      label: "Bold",
      showInCompact: true,
    },
    {
      style: "ITALIC",
      icon: (
        <img
          src="/icons/itallic.svg"
          alt="Italic"
          style={{
            width: "18px",
            height: "18px",
            filter: "brightness(0) invert(1)",
          }}
        />
      ),
      label: "Italic",
      showInCompact: true,
    },
    {
      style: "STRIKETHROUGH",
      icon: (
        <img
          src="/icons/icon-area.svg"
          alt="Strikethrough"
          style={{
            width: "18px",
            height: "18px",
            filter: "brightness(0) invert(1)",
          }}
        />
      ),
      label: "Strikethrough",
      showInCompact: true,
    },
    {
      style: "UNDERLINE",
      icon: (
        <img
          src="/icons/underline.svg"
          alt="Underline"
          style={{
            width: "18px",
            height: "18px",
            filter: "brightness(0) invert(1)",
          }}
        />
      ),
      label: "Underline",
      showInCompact: true,
    },
    {
      style: "unordered-list-item",
      icon: (
        <img
          src="/icons/list-icon.svg"
          alt="ul"
          style={{
            width: "18px",
            height: "18px",
            filter: "brightness(0) invert(1)",
          }}
        />
      ),
      label: "Bullet List",
      showInCompact: true,
    },
    {
      style: "ordered-list-item",
      icon: (
        <img
          src="/icons/number-list-icon.svg"
          alt="ol"
          style={{
            width: "18px",
            height: "18px",
            filter: "brightness(0) invert(1)",
          }}
        />
      ),
      label: "Numbered List",
      showInCompact: true,
    },
    {
      action: "undo",
      icon: <UndoIcon />,
      label: "Undo",
      showInCompact: true,
    },
    {
      action: "redo",
      icon: <RedoIcon />,
      label: "Redo",
      showInCompact: true,
    },
    {
      action: "emoji",
      icon: <EmojiSelectorIcon />,
      label: "Emoji",
      showInCompact: true,
    },
    {
      action: "expand",
      icon: <ExpandIcon />,
      label: "Expand",
      showInCompact: true,
    },
    {
      action: "fullscreen",
      icon: <FullscreenIcon />,
      label: "Fullscreen",
      showInCompact: variant === "compact",
    },
    {
      action: "close",
      icon: <CloseIcon />,
      label: "Close",
      showInCompact: variant === "compact",
    },
  ];

  // Action buttons (shown only when not expanded)
  const actionButtons = [
    {
      action: "expand",
      icon: <ExpandEditorIcon />,
      label: "Expand Editor",
    },
    {
      action: "file",
      icon: <FileSelectorIcon />,
      label: "Add File",
    },
    {
      action: "emoji",
      icon: <EmojiSelectorIcon />,
      label: "Add Emoji",
    },
    {
      action: "bookmark",
      icon: <BookmarkIcon />,
      label: "Bookmark",
    },
    {
      action: "gif",
      icon: <GifSelectorIcon />,
      label: "Add GIF",
    },
    {
      action: "file-chooser",
      icon: <FileSelectorIcon />,
      label: "Choose File",
    },
  ];

  const filteredFormattingButtons = enableMediaActions
    ? formattingButtons
    : formattingButtons.filter((b) => b.action !== "file");

  const filteredActionButtons = enableMediaActions
    ? actionButtons
    : actionButtons.filter(
        (b) => !["file", "gif", "file-chooser"].includes(b.action),
      );

  const recomputeEmojiPopoverPos = () => {
    const btn = emojiButtonRef.current;
    if (!btn) return;

    const btnRect = btn.getBoundingClientRect();
    const viewportW = window.innerWidth || document.documentElement.clientWidth;
    const viewportH =
      window.innerHeight || document.documentElement.clientHeight;

    const padding = 8;
    const gap = 8;

    // Measure actual picker size when available; fall back to defaults.
    const pickerRect = emojiPopoverRef.current?.getBoundingClientRect();
    const pickerWidth = Math.max(260, Math.min(pickerRect?.width || 330, 420));
    const pickerHeight = Math.max(
      240,
      Math.min(pickerRect?.height || 360, 520),
    );

    // Horizontal: clamp within viewport.
    const rawLeft = btnRect.left;
    const minLeft = padding;
    const maxLeft = Math.max(padding, viewportW - pickerWidth - padding);
    const left = Math.min(Math.max(rawLeft, minLeft), maxLeft);

    // Vertical: prefer requested placement, but flip if not enough space.
    const spaceAbove = btnRect.top - padding;
    const spaceBelow = viewportH - btnRect.bottom - padding;
    const prefer = emojiPlacement === "above" ? "above" : "below";

    let placement = prefer;
    if (
      prefer === "above" &&
      spaceAbove < pickerHeight + gap &&
      spaceBelow > spaceAbove
    ) {
      placement = "below";
    } else if (
      prefer === "below" &&
      spaceBelow < pickerHeight + gap &&
      spaceAbove > spaceBelow
    ) {
      placement = "above";
    }

    let top =
      placement === "above"
        ? btnRect.top - pickerHeight - gap
        : btnRect.bottom + gap;

    // Clamp to viewport so it never renders offscreen.
    const minTop = padding;
    const maxTop = Math.max(padding, viewportH - pickerHeight - padding);
    top = Math.min(Math.max(top, minTop), maxTop);

    setEmojiPopoverPos({ left, top, placement });
  };

  // Anchor the emoji tray to the emoji icon and keep it in viewport.
  useLayoutEffect(() => {
    if (!showEmojiPicker) return;
    recomputeEmojiPopoverPos();
    const raf = requestAnimationFrame(() => recomputeEmojiPopoverPos());
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmojiPicker, emojiPlacement]);

  useLayoutEffect(() => {
    if (!showEmojiPicker) return;
    const onWin = () => recomputeEmojiPopoverPos();
    window.addEventListener("scroll", onWin, true);
    window.addEventListener("resize", onWin);
    return () => {
      window.removeEventListener("scroll", onWin, true);
      window.removeEventListener("resize", onWin);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmojiPicker, emojiPlacement]);

  const recomputeTagPopoverPos = () => {
    const btn = tagAnchorRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const viewportW = window.innerWidth || document.documentElement.clientWidth;
    const viewportH =
      window.innerHeight || document.documentElement.clientHeight;
    const padding = 8;
    const gap = 8;
    const minW = 240;
    const maxW = 360;
    const width = Math.min(
      maxW,
      Math.max(minW, rect.width * 4),
      viewportW - 2 * padding,
    );
    let left = rect.left;
    if (left + width + padding > viewportW) {
      left = Math.max(padding, viewportW - width - padding);
    }
    left = Math.max(padding, left);

    const desiredMaxHeight = 300;
    const spaceBelow = viewportH - rect.bottom - padding;
    const spaceAbove = rect.top - padding;

    let placement = "below";
    if (spaceBelow < 140 && spaceAbove > spaceBelow) {
      placement = "above";
    }

    let top = 0;
    let maxHeight = desiredMaxHeight;

    if (placement === "below") {
      top = rect.bottom + gap;
      maxHeight = Math.max(
        120,
        Math.min(desiredMaxHeight, viewportH - top - padding),
      );
    } else {
      maxHeight = Math.max(120, Math.min(desiredMaxHeight, spaceAbove - gap));
      top = Math.max(padding, rect.top - maxHeight - gap);
    }

    setTagPopoverPos({ left, top, width, maxHeight });
  };

  useLayoutEffect(() => {
    if (!showTagModal || externalTagPopover) return;
    recomputeTagPopoverPos();
    const raf = requestAnimationFrame(() => recomputeTagPopoverPos());
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTagModal, externalTagPopover, members?.length]);

  useLayoutEffect(() => {
    if (!showTagModal || externalTagPopover) return;
    const onWin = () => recomputeTagPopoverPos();
    window.addEventListener("scroll", onWin, true);
    window.addEventListener("resize", onWin);
    return () => {
      window.removeEventListener("scroll", onWin, true);
      window.removeEventListener("resize", onWin);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTagModal, externalTagPopover]);

  // Collapse button (shown before formatting buttons when expanded)
  const collapseButton = {
    action: "collapse",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12.5 16.6004L7.06667 11.1671C6.425 10.5254 6.425 9.47539 7.06667 8.83372L12.5 3.40039"
          stroke="white"
          strokeWidth="1.5"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    label: "Collapse Editor",
  };

  const buttonsToShow =
    variant === "compact"
      ? // Always show all compact formatting buttons; hide the "expand editor" button.
        filteredFormattingButtons.filter(
          (b) => b.showInCompact && b.action !== "expand",
        )
      : isExpanded
        ? [
            collapseButton,
            ...filteredFormattingButtons.filter(
              (b) => !["expand", "fullscreen", "close"].includes(b.action),
            ),
          ]
        : filteredActionButtons;

  const compactScrollRef = useRef(null);
  const [canCompactScrollLeft, setCanCompactScrollLeft] = useState(false);
  const [canCompactScrollRight, setCanCompactScrollRight] = useState(false);

  const updateCompactScrollState = () => {
    if (variant !== "compact") return;
    const el = compactScrollRef.current;
    if (!el) return;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    setCanCompactScrollLeft(el.scrollLeft > 0);
    setCanCompactScrollRight(el.scrollLeft < maxScrollLeft - 1);
  };

  useLayoutEffect(() => {
    if (variant !== "compact") return;
    updateCompactScrollState();
    const el = compactScrollRef.current;
    if (!el) return;

    const onScroll = () => updateCompactScrollState();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, buttonsToShow.length]);

  const scrollCompactToolbar = (dir) => {
    const el = compactScrollRef.current;
    if (!el) return;
    const amount = Math.max(200, Math.round(el.clientWidth * 0.7));
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  const handleButtonClick = (button) => {
    if (button.action) {
      switch (button.action) {
        case "expand":
        case "collapse":
          onExpandEditor();
          break;
        case "file":
          onFileSelect();
          break;
        case "emoji":
          onEmojiSelect();
          break;
        case "bookmark":
          onBookmarkIcon();
          break;
        case "undo":
          handleUndo();
          break;
        case "redo":
          handleRedo();
          break;
        case "gif":
          onFileSelect();
          break;
        case "file-chooser":
          onFileSelect();
          break;
        case "fullscreen":
          if (onFullscreen) onFullscreen();
          break;
        case "close":
          if (onClose) onClose();
          break;
        default:
          break;
      }
    }
  };

  return (
    <div
      className={`custom-toolbar position-relative ${variant === "compact" ? "compact-toolbar" : ""}`}
      ref={toolbarRef}
      style={{
        margin: "0px",
        border: "none",
        background: "transparent",
        padding: "0px",
      }}
    >
      {showEmojiPicker &&
        createPortal(
          <div
            ref={emojiPopoverRef}
            style={{
              position: "fixed",
              left: `${emojiPopoverPos.left}px`,
              top: `${emojiPopoverPos.top}px`,
              zIndex: 100000,
            }}
          >
            <emoji-picker ref={emojiPickerRef}></emoji-picker>
          </div>,
          document.body,
        )}
      {variant === "compact" ? (
        <>
          <button
            className="custom-toolbar-button"
            style={{
              opacity: canCompactScrollLeft ? 1 : 0,
              pointerEvents: canCompactScrollLeft ? "auto" : "none",
            }}
            onMouseDown={(e) => {
              // Don't steal focus from the draft editor
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={() => scrollCompactToolbar(-1)}
            aria-label="Scroll toolbar left"
            title="Scroll toolbar left"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <div ref={compactScrollRef} className="compact-toolbar-scroll">
            {buttonsToShow.map((button, index) => {
              const isActive =
                button.style === "unordered-list-item" ||
                button.style === "ordered-list-item"
                  ? editorState
                      .getCurrentContent()
                      .getBlockForKey(editorState.getSelection().getStartKey())
                      .getType() === button.style
                  : editorState.getCurrentInlineStyle().has(button.style);

              if (button.action === "emoji") {
                return (
                  <React.Fragment key={index}>
                    <button
                      key={index}
                      id="emojiButton"
                      ref={emojiButtonRef}
                      className={`custom-toolbar-button ${isActive ? "active" : ""}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (button.style) {
                          if (
                            [
                              "BOLD",
                              "ITALIC",
                              "UNDERLINE",
                              "STRIKETHROUGH",
                            ].includes(button.style)
                          ) {
                            onToggleInlineStyle(button.style);
                          } else {
                            onToggleBlockType(button.style);
                          }
                        }
                      }}
                      onClick={() => button.action && handleButtonClick(button)}
                      title={button.label}
                    >
                      {button.icon}
                    </button>
                  </React.Fragment>
                );
              }
              if (button.action === "bookmark") {
                return (
                  <React.Fragment key={index}>
                    <button
                      key={index}
                      id="post-toolbar-tag-members"
                      ref={tagAnchorRef}
                      className={`custom-toolbar-button ${isActive ? "active" : ""}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (button.style) {
                          if (
                            [
                              "BOLD",
                              "ITALIC",
                              "UNDERLINE",
                              "STRIKETHROUGH",
                            ].includes(button.style)
                          ) {
                            onToggleInlineStyle(button.style);
                          } else {
                            onToggleBlockType(button.style);
                          }
                        }
                      }}
                      onClick={() => button.action && handleButtonClick(button)}
                      title={button.label}
                    >
                      {button.icon}
                    </button>
                  </React.Fragment>
                );
              }
              return (
                <button
                  key={index}
                  className={`custom-toolbar-button ${isActive ? "active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (button.style) {
                      if (
                        [
                          "BOLD",
                          "ITALIC",
                          "UNDERLINE",
                          "STRIKETHROUGH",
                        ].includes(button.style)
                      ) {
                        onToggleInlineStyle(button.style);
                      } else {
                        onToggleBlockType(button.style);
                      }
                    }
                  }}
                  onClick={() => button.action && handleButtonClick(button)}
                  title={button.label}
                >
                  {button.icon}
                </button>
              );
            })}
          </div>

          <button
            className="custom-toolbar-button"
            style={{
              opacity: canCompactScrollRight ? 1 : 0,
              pointerEvents: canCompactScrollRight ? "auto" : "none",
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={() => scrollCompactToolbar(1)}
            aria-label="Scroll toolbar right"
            title="Scroll toolbar right"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </>
      ) : (
        buttonsToShow.map((button, index) => {
          const isActive =
            button.style === "unordered-list-item" ||
            button.style === "ordered-list-item"
              ? editorState
                  .getCurrentContent()
                  .getBlockForKey(editorState.getSelection().getStartKey())
                  .getType() === button.style
              : editorState.getCurrentInlineStyle().has(button.style);

          if (button.action === "emoji") {
            return (
              <React.Fragment key={index}>
                <button
                  key={index}
                  id="emojiButton"
                  ref={emojiButtonRef}
                  className={`custom-toolbar-button ${isActive ? "active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (button.style) {
                      if (
                        [
                          "BOLD",
                          "ITALIC",
                          "UNDERLINE",
                          "STRIKETHROUGH",
                        ].includes(button.style)
                      ) {
                        onToggleInlineStyle(button.style);
                      } else {
                        onToggleBlockType(button.style);
                      }
                    }
                  }}
                  onClick={() => button.action && handleButtonClick(button)}
                  title={button.label}
                >
                  {button.icon}
                </button>
              </React.Fragment>
            );
          }
          if (button.action === "bookmark") {
            return (
              <React.Fragment key={index}>
                <button
                  key={index}
                  id="post-toolbar-tag-members"
                  ref={tagAnchorRef}
                  className={`custom-toolbar-button ${isActive ? "active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (button.style) {
                      if (
                        [
                          "BOLD",
                          "ITALIC",
                          "UNDERLINE",
                          "STRIKETHROUGH",
                        ].includes(button.style)
                      ) {
                        onToggleInlineStyle(button.style);
                      } else {
                        onToggleBlockType(button.style);
                      }
                    }
                  }}
                  onClick={() => button.action && handleButtonClick(button)}
                  title={button.label}
                >
                  {button.icon}
                </button>
              </React.Fragment>
            );
          }
          return (
            <button
              key={index}
              className={`custom-toolbar-button ${isActive ? "active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                if (button.style) {
                  if (
                    ["BOLD", "ITALIC", "UNDERLINE", "STRIKETHROUGH"].includes(
                      button.style,
                    )
                  ) {
                    onToggleInlineStyle(button.style);
                  } else {
                    onToggleBlockType(button.style);
                  }
                }
              }}
              onClick={() => button.action && handleButtonClick(button)}
              title={button.label}
            >
              {button.icon}
            </button>
          );
        })
      )}
      {showTagModal &&
        !externalTagPopover &&
        createPortal(
          <div
            ref={tagDropdownRef}
            className="d-flex flex-column gap-2 p-3"
            style={{
              position: "fixed",
              left: `${tagPopoverPos.left}px`,
              top: `${tagPopoverPos.top}px`,
              width: `${tagPopoverPos.width}px`,
              maxHeight: `${tagPopoverPos.maxHeight}px`,
              overflowY: "auto",
              zIndex: 100000,
              backgroundColor: "rgb(28, 42, 55)",
              border: "1px solid rgb(42, 64, 82)",
              borderRadius: "10px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
              scrollbarWidth: "thin",
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {isLoadingMembers ? (
              <div
                style={{
                  fontSize: "13px",
                  color: "rgba(255,255,255,0.7)",
                }}
              >
                Loading members...
              </div>
            ) : (
              <>
                {(members || [])
                  .filter((member) => member.user?.id !== userProfile?.id)
                  .map((member) => (
                    <div
                      key={member.user.id}
                      className="gap-2"
                      style={{
                        fontSize: "14px",
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                      onClick={() => handleTagUserClicked(member.user.id)}
                    >
                      <img
                        src={member.user.image || "/icons/james.svg"}
                        alt={member.user.full_name}
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                      <span
                        style={{
                          fontWeight: "600",
                          fontSize: "12px",
                          color: "white",
                        }}
                      >
                        {member.user.full_name}
                      </span>
                    </div>
                  ))}
                {(members || []).filter(
                  (member) => member.user?.id !== userProfile?.id,
                ).length === 0 && (
                  <div
                    style={{
                      fontSize: "13px",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    No members available for tagging.
                  </div>
                )}
              </>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

// Icon components
const ExpandEditorIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.2251 5.97409V4.45742C2.2251 3.49909 3.0001 2.73242 3.9501 2.73242H16.0501C17.0084 2.73242 17.7751 3.50742 17.7751 4.45742V5.97409"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 17.2674V3.42578"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.7168 17.2676H13.2835"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CollapseEditorIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Insert your collapse editor icon here (reverse of expand) */}
    <path
      d="M2.2251 14.0259V15.5426C2.2251 16.5009 3.0001 17.2676 3.9501 17.2676H16.0501C17.0084 17.2676 17.7751 16.4926 17.7751 15.5426V14.0259"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 2.7326V16.5742"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.7168 2.73242H13.2835"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FileSelectorIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.49984 18.3327H12.4998C16.6665 18.3327 18.3332 16.666 18.3332 12.4993V7.49935C18.3332 3.33268 16.6665 1.66602 12.4998 1.66602H7.49984C3.33317 1.66602 1.6665 3.33268 1.6665 7.49935V12.4993C1.6665 16.666 3.33317 18.3327 7.49984 18.3327Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.50016 8.33333C8.42064 8.33333 9.16683 7.58714 9.16683 6.66667C9.16683 5.74619 8.42064 5 7.50016 5C6.57969 5 5.8335 5.74619 5.8335 6.66667C5.8335 7.58714 6.57969 8.33333 7.50016 8.33333Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2.2251 15.7918L6.33343 13.0335C6.99176 12.5918 7.94176 12.6418 8.53343 13.1501L8.80843 13.3918C9.45843 13.9501 10.5084 13.9501 11.1584 13.3918L14.6251 10.4168C15.2751 9.85846 16.3251 9.85846 16.9751 10.4168L18.3334 11.5835"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EmojiSelectorIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.49984 18.3327H12.4998C16.6665 18.3327 18.3332 16.666 18.3332 12.4993V7.49935C18.3332 3.33268 16.6665 1.66602 12.4998 1.66602H7.49984C3.33317 1.66602 1.6665 3.33268 1.6665 7.49935V12.4993C1.6665 16.666 3.33317 18.3327 7.49984 18.3327Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.9165 8.125C13.6069 8.125 14.1665 7.56536 14.1665 6.875C14.1665 6.18464 13.6069 5.625 12.9165 5.625C12.2261 5.625 11.6665 6.18464 11.6665 6.875C11.6665 7.56536 12.2261 8.125 12.9165 8.125Z"
      stroke="white"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.0835 8.125C7.77385 8.125 8.3335 7.56536 8.3335 6.875C8.3335 6.18464 7.77385 5.625 7.0835 5.625C6.39314 5.625 5.8335 6.18464 5.8335 6.875C5.8335 7.56536 6.39314 8.125 7.0835 8.125Z"
      stroke="white"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 11.084H13C13.4167 11.084 13.75 11.4173 13.75 11.834C13.75 13.909 12.075 15.584 10 15.584C7.925 15.584 6.25 13.909 6.25 11.834C6.25 11.4173 6.58333 11.084 7 11.084Z"
      stroke="white"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BookmarkIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.47483 12.7492L7.24983 16.5242C8.79983 18.0742 11.3165 18.0742 12.8748 16.5242L16.5332 12.8659C18.0832 11.3159 18.0832 8.79919 16.5332 7.24085L12.7498 3.47419C11.9582 2.68252 10.8665 2.25752 9.74983 2.31585L5.58316 2.51585C3.9165 2.59085 2.5915 3.91585 2.50816 5.57419L2.30816 9.74085C2.25816 10.8659 2.68316 11.9575 3.47483 12.7492Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.91634 10.0007C9.06693 10.0007 9.99967 9.06791 9.99967 7.91732C9.99967 6.76672 9.06693 5.83398 7.91634 5.83398C6.76575 5.83398 5.83301 6.76672 5.83301 7.91732C5.83301 9.06791 6.76575 10.0007 7.91634 10.0007Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);
const GifSelectorIcon = () => (
  <img
    src="icons/post-gif.svg"
    alt="GIF"
    style={{ width: "20px", height: "20px" }}
  />
);

const UndoIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 7v6h6" />
    <path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13" />
  </svg>
);

const RedoIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 7v6h-6" />
    <path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13" />
  </svg>
);

const ExpandIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const FullscreenIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 01-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default PostToolbar;
